<template>
  <div id="curate" class="pt-4">
    <div v-if="info.isPhotoOwnedByUserId" class="text-center">
      <h3 class="text-h6 font-weight-bold">
        <v-icon medium class="mr-2">fal fa-ban</v-icon>
        You cannot curate your own photos
      </h3>
    </div>
    <div v-else>
      <!-- Already curated -->
      <div v-if="info.isCurated" class="text-center">
        <h3 class="text-h6 font-weight-bold">
          <v-icon medium class="mr-2">fal fa-thumbs-up</v-icon>
          You curated this photo {{ info.curationDate | timeago }}
        </h3>
      </div>
      <!-- End Already curated -->
      <!-- Blocked user -->
      <div v-else-if="info.isThisUserBlocked" class="text-center">
        <h3 class="text-h6 font-weight-bold">
          <v-icon medium class="mr-2">fal fa-ban</v-icon>
          You cannot curate this user's photo
        </h3>
      </div>
      <!-- End Blocked user -->
      <!-- All Users -->
      <div v-else>
        <div>
          <v-alert
            v-if="
              photo.numberOfComments < 3 &&
              photo.statusName === types.photo.status.inCuration &&
              me.role !== types.user.role.curator
            "
            type="info"
            dark
            icon="fal fa-info-circle fa-md"
            dismissible
            dense
            close-icon="fal fa-times fa-md"
            class="text-body-2"
            >Comment on this photo to earn
            <strong>{{ getBonusCommentTokens }} more</strong> Tokens</v-alert
          >
          <!-- Quick Curation -->
          <div class="mb-8">
            <div class="curate-title-container">
              <h3 class="text-subtitle-1 font-weight-bold mb-2">
                Photo evaluation
                <small v-if="inCuration" class="ml-2 font-weight-regular"
                  >(
                  <v-icon color="gold" x-small>fal fa-coins</v-icon>
                  <span class="font-weight-bold">+2</span> Tokens )</small
                >
              </h3>
            </div>
            <div>
              <!-- Buttons -->
              <div class="mb-6">
                <curation-group-title
                  :data="curationGroupTitleData.composition"
                />
                <v-btn-toggle
                  v-model="compositionRating"
                  active-class="selected"
                  class="d-flex"
                >
                  <v-btn
                    v-for="i in 5"
                    :key="i"
                    :disabled="isButtonDisabled(i, compositionRating)"
                    :value="i"
                    class="font-weight-bold rating-btn"
                    block
                    small
                  >
                    {{ i }}
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div class="mb-6">
                <curation-group-title
                  :data="curationGroupTitleData.technique"
                />
                <v-btn-toggle
                  v-model="techniqueRating"
                  active-class="selected"
                  class="d-flex"
                >
                  <v-btn
                    v-for="i in 5"
                    :key="i"
                    :disabled="isButtonDisabled(i, techniqueRating)"
                    :value="i"
                    class="font-weight-bold rating-btn"
                    block
                    small
                  >
                    {{ i }}
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div class="mb-6">
                <curation-group-title
                  :data="curationGroupTitleData.creativity"
                />
                <v-btn-toggle
                  v-model="creativityRating"
                  active-class="selected"
                  class="d-flex"
                >
                  <v-btn
                    v-for="i in 5"
                    :key="i"
                    :disabled="isButtonDisabled(i, creativityRating)"
                    :value="i"
                    class="font-weight-bold rating-btn"
                    block
                    small
                  >
                    {{ i }}
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div class="mb-6">
                <curation-group-title :data="curationGroupTitleData.content" />
                <v-btn-toggle
                  v-model="contentRating"
                  active-class="selected"
                  class="d-flex"
                >
                  <v-btn
                    v-for="i in 5"
                    :key="i"
                    :disabled="isButtonDisabled(i, contentRating)"
                    :value="i"
                    class="font-weight-bold rating-btn"
                    block
                    small
                  >
                    {{ i }}
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div class="mb-6">
                <curation-group-title :data="curationGroupTitleData.lighting" />
                <v-btn-toggle
                  v-model="lightingRating"
                  active-class="selected"
                  class="d-flex"
                >
                  <v-btn
                    v-for="i in 5"
                    :key="i"
                    :disabled="isButtonDisabled(i, lightingRating)"
                    :value="i"
                    class="font-weight-bold rating-btn"
                    block
                    small
                  >
                    {{ i }}
                  </v-btn>
                </v-btn-toggle>
              </div>
              <!-- Curation overall -->
              <div class="text-center">
                <p>
                  Overall evaluation<span v-if="displayOverallValuation"
                    >:
                    <span class="font-weight-bold">
                      {{ averageCurationData.text }}
                    </span>
                  </span>
                </p>
                <div>
                  <v-progress-linear
                    :color="averageCurationData.color"
                    :value="averageCurationPercentage"
                    :height="7"
                    rounded
                  ></v-progress-linear>
                </div>
              </div>
              <!-- End Curation overall -->
              <!-- End Buttons -->
            </div>
          </div>
          <!-- End Quick Curation -->
          <!-- Comment Form -->
          <div>
            <div>
              <h3 class="text-subtitle-1 font-weight-bold mb-2">Comment</h3>
            </div>
            <div>
              <v-dialog
                v-if="photo.statusName === types.photo.status.inCuration"
                v-model="commentDialog"
                width="400"
              >
                <template #activator="{ on }">
                  <span class="d-block text-caption mb-2 clickable" v-on="on">
                    <i class="fal fa-info-circle mr-2" />How many Tokens do I
                    earn with a comment?
                  </span>
                </template>
                <v-card outlined>
                  <v-card-text class="pt-6">
                    <p>
                      We believe that your hard work deserves more than a "nice
                      one" comment.
                    </p>
                    <p>
                      But, can this be achieved with three words? We don't think
                      so. And this is the reason behind the minimum of 60
                      characters per comment.
                    </p>
                    <p>Your time is well rewarded with extra Tokens:</p>
                    <p>
                      Min comment length 60 =
                      <v-icon color="gold" x-small class="mr-1"
                        >fal fa-coins</v-icon
                      >
                      x 2
                    </p>
                    <p>
                      Min comment length 120 =
                      <v-icon color="gold" x-small class="mr-1"
                        >fal fa-coins</v-icon
                      >
                      x 4
                    </p>
                    <p>
                      Min comment length 400 =
                      <v-icon color="gold" x-small class="mr-1"
                        >fal fa-coins</v-icon
                      >
                      x 8
                    </p>
                    <p class="text-caption">
                      When commenting on another photographer's photo, you
                      should also aim at giving improvement tips and clearly
                      explaining your thoughts and ideas in as much detail as
                      you can.
                    </p>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text small @click="commentDialog = false">
                      Close
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
            <h3 v-if="photo.statusName === types.photo.status.inCuration" class="text-subtitle-1 font-weight-bold mb-2">
              Comment length Tokens
              <small v-if="inCuration" class="ml-2 font-weight-regular"
                >(
                <v-icon color="gold" x-small>fal fa-coins</v-icon>
                <span class="font-weight-bold">+{{ commentLengthTokens }}</span> Tokens )</small
              >
            </h3>
            <div>
              <v-form
                ref="form"
                v-model="valid"
                lazy-validation
                @submit.prevent="noop"
              >
                <!-- Comment textarea -->
                <v-textarea
                  id="comment"
                  v-model="comment"
                  :rules="commentRules"
                  :loading="true"
                  :label="
                    photo.statusName === types.photo.status.inCuration
                      ? 'Earn extra Tokens by adding a comment'
                      : 'Optional comment'
                  "
                  validate-on-blur
                  hint="English is the preferred language. You can still post comments in your own language and we will automatically translate it for you!"
                  auto-grow
                  outlined
                  counter
                  rows="3"
                  @paste="onPaste"
                  @focus="setAllowKeyboardNavigation(false)"
                  @blur="setAllowKeyboardNavigation(true)"
                >
                  <template #progress>
                    <v-progress-linear
                      :value="progress"
                      height="3"
                      class="mt-1"
                      absolute
                    ></v-progress-linear>
                  </template>
                </v-textarea>
                <!-- End Comment textarea -->
                <!-- Tokens Resume -->
                <h3 v-if="photo.statusName === types.photo.status.inCuration" class="text-subtitle-1 font-weight-bold mb-2">
                  Total Tokens for this curation: <v-icon color="gold" x-small class="ml-2 mr-1">fal fa-coins</v-icon><span class="font-weight-bold">{{ totalTokensForCuration }}</span>
                </h3>
                <div v-if="photo.statusName === types.photo.status.inCuration" class="d-flex flex-wrap">
                  <div v-for="(token, index) in earnableTokens" :key="index">
                    <v-icon x-small :color="index + 1 <= totalTokensForCuration ? 'gold' : 'grey'" class="mr-2">fal fa-coin</v-icon>
                  </div>
                </div>
                <!-- End Tokens Resume -->
                <!-- Curators Vote for Publish or Reject -->
                <template
                  v-if="
                    (me.role === types.user.role.curator || me.isAdmin || me.isArtistCurator) &&
                    photo.statusName === types.photo.status.inCuration
                  "
                  class="py-0"
                  cols="12"
                >
                  <v-checkbox
                    v-if="me.role === types.user.role.curator || me.isAdmin || me.role === types.user.role.legend"
                    v-model="voteForElite"
                    label="Add Vote for Elite"
                    on-icon="fal fa-check-square"
                    off-icon="fal fa-square"
                  ></v-checkbox>
                  <v-btn
                    :class="{ 'mt-4': me.role !== types.user.role.curator && !me.isAdmin && me.role !== types.user.role.legend }"
                    :loading="processing"
                    type="submit"
                    dark
                    block
                    depressed
                    color="success"
                    large
                    class="ma-0 mb-3"
                    @click="curate('publish_photo')"
                  >
                    <v-icon small class="mr-6">fal fa-angle-double-up</v-icon>
                    Upvote for prime
                  </v-btn>
                  <v-btn
                    :loading="processing"
                    type="submit"
                    dark
                    block
                    depressed
                    color="info"
                    large
                    class="ma-0 mb-3"
                    @click="curate('neutral_photo')"
                  >
                    <v-icon small class="mr-6"
                      >fal fa-angle-double-right</v-icon
                    >
                    Neutral vote
                  </v-btn>
                  <v-btn
                    :loading="processing"
                    type="submit"
                    dark
                    block
                    depressed
                    color="error"
                    large
                    class="ma-0"
                    @click="curate('reject_photo')"
                  >
                    <v-icon small class="mr-6">fal fa-angle-double-down</v-icon>
                    Demoting vote
                  </v-btn>
                </template>
                <!-- End Curators Vote for Publish or Reject -->
                <!-- Submit common users -->
                <template
                  v-else-if="
                    me.role !== types.user.role.curator ||
                    (me.role === types.user.role.curator &&
                      photo.statusName !== types.photo.status.inCuration)
                  "
                  cols="12"
                  offset-sm="8"
                  sm="4"
                  offset-lg="9"
                  lg="3"
                  class="py-0"
                >
                  <v-btn
                    :loading="processing"
                    type="submit"
                    dark
                    block
                    depressed
                    large
                    class="ma-0 mt-6"
                    @click="curate()"
                  >
                    <v-icon small class="mr-2">fal fa-check</v-icon>
                    Submit
                  </v-btn>
                </template>
                <!-- End Submit common users -->
              </v-form>
            </div>
          </div>
          <!-- End Comment Form -->
        </div>
      </div>
      <!-- End All Users -->
    </div>
  </div>
</template>

<script>
import { curationMixin } from '@/mixins/curationMixin'

export default {
  name: 'CurateSection',
  mixins: [curationMixin],
  props: {
    me: {
      type: [Object, Boolean],
      default: () => {}
    },
    photo: {
      type: Object,
      default: () => {}
    },
    info: {
      type: Object,
      default: () => {}
    },
    full: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
.v-messages__message {
  margin-top: 3px;
}
.curate-title-container {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
}
.curate-title-container button {
  margin: 0;
}
.v-application .selected {
  background-color: #232323 !important;
  color: white !important;
}
.clickable {
  cursor: pointer;
  text-decoration: underline;
}
.rating-btn {
  min-width: auto !important;
}
@media screen and (max-width: 480px) {
  .curate-title-container {
    display: initial;
  }
  .curate-title-container button {
    margin: 12px 0;
  }
}
</style>
